<template>
  <div class="promo-header-banner">
    <div class="q-container">
      <div class="promo-banner-content">
        <!-- Logo -->
        <q-avatar size="24px" class="q-mr-sm">
          <q-img 
            :src="bannerData.logoUrl" 
            @error="onImgError" 
            alt="Store Logo" 
            no-spinner
          />
        </q-avatar>
        <!-- Desktop Layout -->
        <div class="promo-banner-middle promo-banner-desktop">
          <span class="promo-badge">Limited Time!</span>
          <span class="promo-banner-text">{{ bannerData.text }}</span>
          <q-btn 
            flat 
            dense 
            class="promo-shop-btn" 
            :label="bannerData.buttonText" 
            @click="handleClick(bannerData)"
          />
        </div>
        <!-- Mobile Layout: visually distinct, mobile-first design -->
        <div class="promo-banner-middle promo-banner-mobile">
          <div class="mobile-promo-left" style="margin-left:-55px;">
            <q-icon name="notifications_active" class="mobile-alarm-icon" color="red" size="20px" />
            <q-avatar size="22px" class="q-mr-xs">
              <q-img :src="bannerData.logoUrl" @error="onImgError" alt="Store Logo" no-spinner />
            </q-avatar>
          </div>
          <div class="mobile-promo-center">
            <span class="promo-banner-text">{{ bannerData.text }}</span>
          </div>
          <div class="mobile-promo-right">
            <q-btn 
              flat 
              dense 
              class="promo-shop-btn" 
              :label="bannerData.buttonText" 
              @click="handleClick(bannerData)"
            />
          </div>
        </div>
        <q-space />
        <!-- Close button on the right -->
        <q-btn 
          flat 
          dense 
          round 
          icon="close" 
          size="xs" 
          class="promo-close-btn"
          @click="closeBanner" 
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useQuasar } from 'quasar';

const $q = useQuasar()

const bannerData = {
  active: true,
  logoUrl: 'https://cdn.hostinger.com/hostinger-logos/2019/logo-light.svg',
  text: 'Get 80% off on Hostinger',
  buttonText: 'SHOP NOW',
  link: 'https://hostinger.com/special-offer',
  couponId: 'promo-banner-1',
  trackingId: 'hostinger-promo-banner'
}

// Emit is used for emitting events to the parent component
const emit = defineEmits(['close', 'click']);

// Handle banner click
const handleClick = (banner) => {
  if (!banner) return;
  
  // Emit click event for tracking
  emit('click', banner);
  
  // Use the worker function for redirection
  if (banner.couponId) {
    // Redirect through the worker to track clicks and apply proper redirection
    const goUrl = `/go/${banner.couponId}`;
    window.open(goUrl, '_blank');
  } else if (banner.link) {
    // Fallback to direct link if no couponId is provided
    console.warn('Banner missing couponId, using direct link instead');
    window.open(banner.link, '_blank');
  }
};

// Close the banner
const closeBanner = () => {
  // Simply emit the close event to let App.vue handle it
  emit('close');
};

// Handle image errors
const onImgError = (err) => {
  // Set default placeholder image if available
  if (err && err.target) {
    err.target.src = '/images/placeholder.png';
  }
};

// No need for onMounted hook since we always show the banner on page load
</script>

<style lang="scss" scoped>
/* Promotional Header Banner Styles */
.promo-header-banner {
  background: linear-gradient(90deg, #4f8cff 0%, #6a82fb 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(80, 120, 255, 0.15);
  border-radius: 0 0 16px 16px;
  animation: bannerFadeIn 0.7s cubic-bezier(0.4,0,0.2,1);
  height: 40px;
  font-size: 1rem;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  position: relative;
}

@keyframes bannerFadeIn {
  from { opacity: 0; transform: translateY(-20px);}
  to { opacity: 1; transform: translateY(0);}
}

.promo-header-banner::before {
  content: '';
  position: absolute;
  top: -20%;
  right: -10%;
  width: 120px;
  height: 150%;
  background: rgba(255, 255, 255, 0.13);
  transform: rotate(35deg);
  pointer-events: none;
}

.promo-banner-content {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  position: relative;
  max-width: 1280px;
  margin: 0 auto;
  gap: 12px;
}

.promo-banner-middle {
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: center;
  height: 100%;
  padding-left: 180px;
  position: relative;
}

.promo-badge {
  background: #ff5252;
  color: #fff;
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  margin-right: 10px;
  font-weight: bold;
  animation: pulse 1.5s infinite;
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.15);
  letter-spacing: 0.5px;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(255, 200, 50, 0.5);}
  70% { box-shadow: 0 0 0 10px rgba(255, 200, 50, 0);}
  100% { box-shadow: 0 0 0 0 rgba(255, 200, 50, 0);}
}

.promo-banner-text {
  font-weight: 600;
  letter-spacing: 0.4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
  margin-right: 24px;
  line-height: 1.1;
  text-shadow: 0 1px 4px rgba(0,0,0,0.10);
}

.promo-shop-btn {
  background: linear-gradient(90deg, #ffb347 0%, #ffcc33 100%);
  color: #222;
  border-radius: 6px;
  font-weight: bold;
  font-size: 0.92rem;
  letter-spacing: 0.5px;
  padding: 0 18px;
  height: 28px;
  min-height: 28px;
  box-shadow: 0 2px 8px rgba(255, 200, 50, 0.15);
  border: none;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  text-shadow: 0 1px 2px rgba(255,255,255,0.15);
  animation: pulse 1.5s infinite;
}
.promo-shop-btn:hover {
  background: linear-gradient(90deg, #ffd700 0%, #ffb347 100%);
  box-shadow: 0 4px 16px rgba(255, 200, 50, 0.25);
  transform: translateY(-2px) scale(1.04);
}

.q-avatar.q-mr-sm {
  box-shadow: 0 2px 8px rgba(80, 120, 255, 0.18);
}

.promo-close-btn {
  color: #fff;
  background: rgba(0,0,0,0.15);
  border-radius: 50%;
  font-size: 1.1rem;
  margin-left: 10px;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px rgba(80, 120, 255, 0.10);
}
.promo-close-btn:hover {
  background: rgba(255,255,255,0.25);
  color: #ff5252;
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.15);
}

.promo-banner-desktop {
  display: flex;
}
.promo-banner-mobile {
  display: none;
}
@media (max-width: 599px) {
  .promo-header-banner {
    height: 56px;
    font-size: 0.92rem;
    border-radius: 0 0 10px 10px;
    box-shadow: 0 2px 8px rgba(80, 120, 255, 0.10);
  }
  .promo-banner-desktop {
    display: none !important;
  }
  .promo-banner-mobile {
    display: flex !important;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0 4px;
    gap: 0;
    min-width: 0;
    background: none;
  }
  .mobile-promo-left {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }
  .mobile-promo-center {
    flex: 1 1 0;
    min-width: 0;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .mobile-promo-right {
    flex-shrink: 0;
    margin-left: 2px;
    display: flex;
    align-items: center;
  }
  .promo-badge {
    background: #ff5252;
    color: #fff;
    border-radius: 6px;
    padding: 1px 6px;
    font-size: 0.7rem;
    margin: 0 0 0 2px;
    font-weight: bold;
    animation: pulse 1.5s infinite;
    box-shadow: 0 1px 4px rgba(255, 82, 82, 0.10);
    letter-spacing: 0.5px;
  }
  .promo-banner-text {
    max-width: 100vw;
    font-size: 0.8rem;
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    letter-spacing: 0.2px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.08);
  }
  .promo-shop-btn {
    font-size: 0.75rem;
    padding: 0 8px;
    height: 24px;
    min-height: 24px;
    margin: 0;
    border-radius: 5px;
    flex-shrink: 0;
    background: linear-gradient(90deg, #ffb347 0%, #ffcc33 100%);
    color: #222;
    font-weight: bold;
    box-shadow: 0 1px 4px rgba(255, 200, 50, 0.10);
    animation: pulse 1.5s infinite;
  }
  .q-avatar.q-mr-xs {
    box-shadow: none;
    background: transparent;
    border-radius: 50%;
    width: 22px;
    height: 22px;
  }
  .mobile-alarm-icon {
    margin-left: 2px;
    margin-right: 2px;
    vertical-align: middle;
    animation: alarmPulse 1.2s infinite;
    filter: drop-shadow(0 0 4px #ff5252cc) drop-shadow(0 0 2px #fff3);
    font-size: 22px !important;
    color: #ff1744 !important;
    background: #fff3;
    border-radius: 50%;
    padding: 2px;
    box-shadow: 0 0 6px 2px #ff525233;
  }
}
@keyframes alarmPulse {
  0% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.18); filter: brightness(1.3); }
  100% { transform: scale(1); filter: brightness(1); }
}
@media (min-width: 600px) and (max-width: 1023px) {
  .promo-banner-middle {
    justify-content: center;
    padding-left: 60px;
  }
}

.q-container {
  width: 100%;
}
</style>
